import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Upload, Download, Trash2, Loader2, Plus, X, FileText, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { qualityNcrAPI } from '@/lib/api';

export const saveBlob = (res, fallback) => {
  const cd = res.headers?.['content-disposition'] || '';
  const star = cd.match(/filename\*=UTF-8''([^;]+)/i);
  const m = cd.match(/filename="?([^";]+)"?/);
  const a = document.createElement('a');
  a.href = URL.createObjectURL(res.data);
  a.download = star ? decodeURIComponent(star[1]) : m ? m[1] : fallback;
  a.click();
};

export const errMsg = async (e, fb) => {
  const d = e.response?.data;
  if (d instanceof Blob) { try { return JSON.parse(await d.text()).detail || fb; } catch { return fb; } }
  return d?.detail || fb;
};

export const fmtDate = (s) => (s ? new Date(s.length <= 10 ? `${s}T00:00:00` : s).toLocaleDateString('en-GB') : '—');

const STATUS_TONE = {
  Open: 'bg-amber-100 text-amber-800', OPEN: 'bg-amber-100 text-amber-800',
  Resolved: 'bg-blue-100 text-blue-800', RESOLVED: 'bg-blue-100 text-blue-800',
  Closed: 'bg-emerald-100 text-emerald-800', CLOSED: 'bg-emerald-100 text-emerald-800',
};
export const StatusBadge = ({ status, testid }) => (
  <Badge variant="outline" className={`border-0 ${STATUS_TONE[status] || ''}`} data-testid={testid}>{status}</Badge>
);

const DEADLINE = {
  done: ['Done', 'bg-emerald-100 text-emerald-700'], overdue: ['Overdue', 'bg-red-100 text-red-700'],
  due_soon: ['Due soon', 'bg-orange-100 text-orange-700'], pending: ['Pending', 'bg-slate-100 text-slate-700'], na: ['—', ''],
};
export const DeadlineBadge = ({ state, due, testid }) => {
  const [label, tone] = DEADLINE[state] || DEADLINE.na;
  return <Badge variant="outline" className={`border-0 text-[10px] ${tone}`} data-testid={testid}>{label}{state !== 'done' && due ? ` · ${fmtDate(due)}` : ''}</Badge>;
};

export const useReasons = () => {
  const [reasons, setReasons] = useState([]);
  useEffect(() => { qualityNcrAPI.reasons().then((r) => setReasons(r.data)).catch(() => {}); }, []);
  return reasons;
};

export const ReasonSelect = ({ value, onChange, reasons, disabled, testid = 'reason-select' }) => (
  <Select value={value || ''} onValueChange={onChange} disabled={disabled}>
    <SelectTrigger data-testid={testid}><SelectValue placeholder="Select rejection reason" /></SelectTrigger>
    <SelectContent>
      {value && !reasons.some((r) => r.name === value) && <SelectItem value={value}>{value}</SelectItem>}
      {reasons.map((r) => <SelectItem key={r.id} value={r.name} data-testid={`${testid}-option-${r.id}`}>{r.name}</SelectItem>)}
    </SelectContent>
  </Select>
);

export const YesNo = ({ label, value, onChange, disabled, testid }) => (
  <div>
    <Label className="text-xs">{label}</Label>
    <Select value={value || ''} onValueChange={onChange} disabled={disabled}>
      <SelectTrigger className="h-9" data-testid={testid}><SelectValue placeholder="—" /></SelectTrigger>
      <SelectContent>
        {value && !['Yes', 'No', 'NA'].includes(value) && <SelectItem value={value}>{value}</SelectItem>}
        {['Yes', 'No', 'NA'].map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  </div>
);

export const Field = ({ label, value, onChange, disabled, testid, rows = 2, required }) => (
  <div>
    <Label className="text-xs">{label}{required && <span className="text-red-500"> *</span>}</Label>
    <Textarea rows={rows} value={value || ''} onChange={(e) => onChange(e.target.value)} disabled={disabled} data-testid={testid} />
  </div>
);

const EMPTY_8D = {
  tracking_number: '', response_due_date: '', team_objective: '', team: [{ name: '', function: '', responsibility: '' }],
  problem_definition: '', containment: '', correction: '', root_cause_occurrence: '', root_cause_non_detection: '',
  permanent_actions: '', implemented: '', implementation_date: '', closure_note: '',
};

const TeamEditor = ({ team, setTeam, disabled }) => (
  <div className="space-y-2" data-testid="eightd-team">
    <Label className="text-xs">D1 · Team (name / function / responsibility)<span className="text-red-500"> *</span></Label>
    {team.map((m, i) => (
      <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2">
        {['name', 'function', 'responsibility'].map((k) => (
          <Input key={k} placeholder={k} value={m[k]} disabled={disabled} className="h-8"
            onChange={(e) => setTeam(team.map((x, j) => (j === i ? { ...x, [k]: e.target.value } : x)))} data-testid={`eightd-team-${k}-${i}`} />
        ))}
        <Button size="icon" variant="ghost" className="h-8 w-8" disabled={disabled} onClick={() => setTeam(team.filter((_, j) => j !== i))} data-testid={`eightd-team-remove-${i}`}><X className="w-3.5 h-3.5" /></Button>
      </div>
    ))}
    <Button size="sm" variant="outline" disabled={disabled} onClick={() => setTeam([...team, { name: '', function: '', responsibility: '' }])} data-testid="eightd-team-add"><Plus className="w-3.5 h-3.5 mr-1" />Add member</Button>
  </div>
);

export const EightDForm = ({ kind, record, onSaved, disabled, defaults }) => {
  const [f, setF] = useState(EMPTY_8D);
  const [saving, setSaving] = useState(false);
  const [dl, setDl] = useState(false);
  useEffect(() => {
    const e = record.eight_d;
    setF(e ? { ...EMPTY_8D, ...e, team: e.team?.length ? e.team : EMPTY_8D.team } : { ...EMPTY_8D, ...defaults });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [record.id, record.eight_d]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const res = await qualityNcrAPI.save8D(kind, record.id, f);
      toast.success(res.data.complete ? '8D saved (complete)' : '8D saved — still incomplete');
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to save 8D'); } finally { setSaving(false); }
  };
  const download = async () => {
    setDl(true);
    try { saveBlob(await qualityNcrAPI.download8D(kind, record.id), '8D.docx'); } catch (e) { toast.error(await errMsg(e, 'Save the 8D first')); } finally { setDl(false); }
  };
  return (
    <Card className="shadow-none" data-testid="eightd-form">
      <CardHeader className="pb-2 flex-row items-center justify-between space-y-0">
        <CardTitle className="text-sm">8D Report (F/QA/15)
          <Badge variant="outline" className={`ml-2 border-0 text-[10px] ${record.eight_d_complete ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100'}`} data-testid="eightd-complete-badge">{record.eight_d_complete ? 'Complete' : 'Incomplete'}</Badge></CardTitle>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={download} disabled={dl || !record.eight_d} data-testid="eightd-download-btn">{dl ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <FileText className="w-3.5 h-3.5 mr-1" />}Download Word</Button>
          <Button size="sm" onClick={save} disabled={saving || disabled} data-testid="eightd-save-btn">{saving ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Save className="w-3.5 h-3.5 mr-1" />}Save 8D</Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid md:grid-cols-3 gap-3">
          <div><Label className="text-xs">Tracking number</Label><Input value={f.tracking_number} onChange={(e) => set('tracking_number')(e.target.value)} disabled={disabled} data-testid="eightd-tracking-input" /></div>
          <div><Label className="text-xs">Response due date</Label><Input type="date" value={f.response_due_date} onChange={(e) => set('response_due_date')(e.target.value)} disabled={disabled} data-testid="eightd-due-input" /></div>
          <div><Label className="text-xs">Team objective</Label><Input value={f.team_objective} onChange={(e) => set('team_objective')(e.target.value)} disabled={disabled} data-testid="eightd-objective-input" /></div>
        </div>
        <TeamEditor team={f.team} setTeam={set('team')} disabled={disabled} />
        <div className="grid md:grid-cols-2 gap-3">
          <Field label="D2 · Problem definition" required value={f.problem_definition} onChange={set('problem_definition')} disabled={disabled} testid="eightd-problem-input" />
          <Field label="D3 · Containment actions" required value={f.containment} onChange={set('containment')} disabled={disabled} testid="eightd-containment-input" />
          <Field label="D4 · Correction" value={f.correction} onChange={set('correction')} disabled={disabled} testid="eightd-correction-input" />
          <Field label="D5 · Root cause — occurrence" required value={f.root_cause_occurrence} onChange={set('root_cause_occurrence')} disabled={disabled} testid="eightd-rc-occ-input" />
          <Field label="D5 · Root cause — non-detection" value={f.root_cause_non_detection} onChange={set('root_cause_non_detection')} disabled={disabled} testid="eightd-rc-det-input" />
          <Field label="D6 · Permanent corrective actions" required value={f.permanent_actions} onChange={set('permanent_actions')} disabled={disabled} testid="eightd-permanent-input" />
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <YesNo label="D7 · Corrective implemented" value={f.implemented} onChange={set('implemented')} disabled={disabled} testid="eightd-implemented-select" />
          <div><Label className="text-xs">Implementation date</Label><Input type="date" value={f.implementation_date} onChange={(e) => set('implementation_date')(e.target.value)} disabled={disabled} data-testid="eightd-impl-date-input" /></div>
        </div>
        <Field label="D8 · Recognise team & close" value={f.closure_note} onChange={set('closure_note')} disabled={disabled} testid="eightd-closure-input" />
        <p className="text-[11px] text-muted-foreground">Problem photos uploaded under Attachments (category “Problem photo”), followed by any quarantine photos from the MRB case (internal NCRs), are placed in the Sketch / Photo box of the Word report (max 5 in total).</p>
      </CardContent>
    </Card>
  );
};

const CATS = { '8d_report': '8D report', photo: 'Problem photo', other: 'Other' };

export const AttachmentsPanel = ({ kind, record, onChanged, disabled }) => {
  const [cat, setCat] = useState('8d_report');
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    try { await qualityNcrAPI.uploadAttachment(kind, record.id, file, cat); toast.success('File attached'); onChanged(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Upload failed'); } finally { setBusy(false); if (ref.current) ref.current.value = ''; }
  };
  const download = async (a) => {
    try { saveBlob(await qualityNcrAPI.downloadAttachment(kind, record.id, a.id), a.filename); } catch (e) { toast.error(await errMsg(e, 'Download failed')); }
  };
  const remove = async (a) => {
    if (!window.confirm(`Remove ${a.filename}?`)) return;
    try { await qualityNcrAPI.deleteAttachment(kind, record.id, a.id); onChanged(); } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const atts = record.attachments || [];
  return (
    <Card className="shadow-none" data-testid="attachments-panel">
      <CardHeader className="pb-2"><CardTitle className="text-sm">Attachments ({atts.length})</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {!disabled && (
          <div className="flex flex-wrap items-end gap-2">
            <div className="w-44"><Label className="text-xs">Category</Label>
              <Select value={cat} onValueChange={setCat}>
                <SelectTrigger className="h-9" data-testid="attachment-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(CATS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
              </Select></div>
            <input ref={ref} type="file" className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,image/*" onChange={(e) => upload(e.target.files?.[0])} data-testid="attachment-file-input" />
            <Button size="sm" variant="outline" className="h-9" onClick={() => ref.current?.click()} disabled={busy} data-testid="attachment-upload-btn">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Upload className="w-3.5 h-3.5 mr-1" />}Attach file</Button>
          </div>
        )}
        {atts.length === 0 && <p className="text-xs text-muted-foreground">No files attached</p>}
        {atts.map((a) => (
          <div key={a.id} className="flex items-center gap-2 text-xs border rounded px-2 py-1.5" data-testid={`attachment-row-${a.id}`}>
            <Badge variant="outline" className="text-[10px]">{CATS[a.category] || a.category}</Badge>
            <span className="flex-1 truncate">{a.filename}</span>
            <span className="text-muted-foreground">{a.uploaded_by} · {fmtDate(a.uploaded_at)}</span>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => download(a)} data-testid={`attachment-download-${a.id}`}><Download className="w-3.5 h-3.5" /></Button>
            {!disabled && <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => remove(a)} data-testid={`attachment-delete-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></Button>}
          </div>
        ))}
      </CardContent>
    </Card>
  );
};

export const HistoryList = ({ items = [] }) => (
  <Card className="shadow-none" data-testid="history-list">
    <CardHeader className="pb-2"><CardTitle className="text-sm">History</CardTitle></CardHeader>
    <CardContent className="space-y-1.5">
      {[...items].reverse().map((h, i) => (
        <div key={i} className="text-xs flex gap-3"><span className="font-mono text-muted-foreground w-36 shrink-0">{new Date(h.at).toLocaleString()}</span>
          <span className="font-medium">{h.action.replace(/_/g, ' ')}</span><span className="text-muted-foreground">{h.detail}</span><span className="ml-auto">{h.by}</span></div>
      ))}
    </CardContent>
  </Card>
);
